<template>
  <VueFinalModal
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    :click-to-close="!submitting"
    :esc-to-close="!submitting"
    classes="documents-modal"
    :content-class="contentClass"
  >
    <template #default>
      <button
        class="modal__close icons-button"
        @click="close"
        title="Chiudi"
        type="button"
        :disabled="submitting"
      >
        <i class="fa-solid fa-xmark"></i>
      </button>

      <h3 class="modal__title">Crea nuovo articolo</h3>

      <!-- ── STEP 1: categoria ──────────────────────────────────────────── -->
      <template v-if="step === 'category'">
        <p class="modal__subtitle">Scegli la categoria del nuovo articolo.</p>
        <div class="cim-step-row">
          <div class="field cim-step-row__select">
            <label>Categoria *</label>
            <StatusSelect
              v-model="category"
              :options="categoryOptions"
              placeholder="Seleziona categoria..."
              placeholderIcon="fa-tag"
            />
          </div>
          <div class="modal-actions cim-step-row__actions">
            <button class="secondary-btn" type="button" @click="close">
              Annulla
            </button>
            <button
              class="primary-btn confirm-btn"
              type="button"
              :disabled="!category"
              @click="goFromCategory"
            >
              Continua
            </button>
          </div>
        </div>
      </template>

      <!-- ── STEP 1b: sotto-categoria (solo dove richiesta, es. piastrelle) ─ -->
      <template v-else-if="step === 'subcategory'">
        <p class="modal__subtitle">
          Scegli la tipologia per «{{ categoryLabel }}».
        </p>
        <div class="cim-step-row">
          <div class="field cim-step-row__select">
            <label>Tipologia *</label>
            <StatusSelect
              v-model="form.subCategory"
              :options="subCategoryOptions"
              placeholder="Seleziona tipologia..."
              placeholderIcon="fa-tag"
            />
          </div>
          <div class="modal-actions cim-step-row__actions">
            <button class="secondary-btn" type="button" @click="backToCategory">
              Indietro
            </button>
            <button
              class="primary-btn confirm-btn"
              type="button"
              :disabled="!form.subCategory"
              @click="goFromSubCategory"
            >
              Continua
            </button>
          </div>
        </div>
      </template>

      <!-- ── STEP 2: form dinamico ──────────────────────────────────────── -->
      <form
        v-else
        class="create-item-form create-item-form-grid"
        @submit.prevent="onSubmit"
      >
        <!-- Dati principali -->
        <div class="field field--span-6">
          <label for="cim-item-code">Codice articolo</label>
          <input
            id="cim-item-code"
            type="text"
            v-model.trim="form.itemCode"
            placeholder="es. ART-001"
            maxlength="255"
          />
          <small style="color: #718096; font-size: 0.75rem"
            >Facoltativo: può essere lasciato vuoto.</small
          >
        </div>
        <div class="field field--span-6">
          <label for="cim-item-name">Nome articolo *</label>
          <input
            id="cim-item-name"
            type="text"
            v-model.trim="form.itemName"
            placeholder="inserisci"
            required
          />
        </div>

        <div class="field" :class="mainColClass">
          <label for="cim-brand">Brand</label>
          <input
            id="cim-brand"
            type="text"
            v-model="form.brand"
            placeholder="inserisci"
          />
        </div>
        <div class="field" :class="mainColClass">
          <label for="cim-serie">Serie</label>
          <input
            id="cim-serie"
            type="text"
            v-model="form.serie"
            placeholder="inserisci"
          />
        </div>
        <!-- Prezzo comune: nascosto per piastrelle (il prezzo/unità del
             blocco piastrelle scrive comunque in form.price, vedi sotto). -->
        <div class="field field--span-3" v-if="category !== 'piastrelle'">
          <label for="cim-price">Prezzo — IVA esclusa</label>
          <input
            id="cim-price"
            type="number"
            step="any"
            min="0"
            v-model.number="form.price"
            placeholder="inserisci"
          />
        </div>
        <div class="field" :class="mainColClass">
          <label for="cim-status">Stato</label>
          <StatusSelect
            v-model="form.articleStatus"
            :options="articleStatusOptions"
          />
        </div>

        <hr />

        <!-- ── Gruppo AREA ── -->
        <template v-if="categoryGroup === 'area'">
          <!-- Piastrelle: campi variabili per tileGroup -->
          <template v-if="category === 'piastrelle'">
            <!-- Elementi Lineari (ML) -->
            <template v-if="tileGroup === 'linear'">
              <div class="field field--span-3">
                <label for="cim-format">Formato</label>
                <input
                  id="cim-format"
                  type="text"
                  v-model="form.formatLabel"
                  placeholder="es. 60x120"
                />
              </div>
              <div class="field field--span-3">
                <label for="cim-thickness">Spessore (mm)</label>
                <input
                  id="cim-thickness"
                  type="number"
                  step="any"
                  min="0"
                  v-model.number="form.thicknessMm"
                  placeholder="es. 10"
                />
              </div>
              <div class="field field--span-3">
                <label for="cim-finish">Finitura</label>
                <input
                  id="cim-finish"
                  type="text"
                  v-model="form.finish"
                  placeholder="inserisci"
                />
              </div>
              <div class="field field--span-3">
                <label for="cim-mlperbox">ML / scatola</label>
                <input
                  id="cim-mlperbox"
                  type="number"
                  step="any"
                  min="0"
                  v-model.number="form.mlPerBox"
                  placeholder="es. 2.5"
                />
              </div>
            </template>

            <!-- Decori / Fondi (M2) -->
            <template v-else-if="tileGroup === 'area'">
              <div class="field field--span-3">
                <label for="cim-format">Formato</label>
                <input
                  id="cim-format"
                  type="text"
                  v-model="form.formatLabel"
                  placeholder="es. 60x120"
                />
              </div>
              <div class="field field--span-3">
                <label for="cim-width">Larghezza (cm)</label>
                <input
                  id="cim-width"
                  type="number"
                  step="any"
                  min="0"
                  v-model.number="form.widthCm"
                  placeholder="inserisci"
                />
              </div>
              <div class="field field--span-3">
                <label for="cim-height">Altezza (cm)</label>
                <input
                  id="cim-height"
                  type="number"
                  step="any"
                  min="0"
                  v-model.number="form.heightCm"
                  placeholder="inserisci"
                />
              </div>
              <div class="field field--span-3">
                <label for="cim-thickness">Spessore (mm)</label>
                <input
                  id="cim-thickness"
                  type="number"
                  step="any"
                  min="0"
                  v-model.number="form.thicknessMm"
                  placeholder="es. 10"
                />
              </div>
              <div class="field field--span-6">
                <label for="cim-finish">Finitura</label>
                <input
                  id="cim-finish"
                  type="text"
                  v-model="form.finish"
                  placeholder="inserisci"
                />
              </div>
              <div class="field field--span-6">
                <label for="cim-sqmperbox">M² / scatola</label>
                <input
                  id="cim-sqmperbox"
                  type="number"
                  step="any"
                  min="0"
                  v-model.number="form.sqmPerBox"
                  placeholder="es. 1.44"
                />
              </div>
            </template>

            <!-- Battiscopa / Listelli / Gradini / Scalini / Altri (PZ) -->
            <template v-else-if="tileGroup === 'piece'">
              <div class="field field--span-3">
                <label for="cim-format">Formato</label>
                <input
                  id="cim-format"
                  type="text"
                  v-model="form.formatLabel"
                  placeholder="es. 60x120"
                />
              </div>
              <div class="field field--span-3">
                <label for="cim-thickness">Spessore (mm)</label>
                <input
                  id="cim-thickness"
                  type="number"
                  step="any"
                  min="0"
                  v-model.number="form.thicknessMm"
                  placeholder="es. 10"
                />
              </div>
              <div class="field field--span-3">
                <label for="cim-finish">Finitura</label>
                <input
                  id="cim-finish"
                  type="text"
                  v-model="form.finish"
                  placeholder="inserisci"
                />
              </div>
              <div class="field field--span-3">
                <label for="cim-sqmperbox">M² / scatola</label>
                <input
                  id="cim-sqmperbox"
                  type="number"
                  step="any"
                  min="0"
                  v-model.number="form.sqmPerBox"
                  placeholder="es. 1.44"
                />
              </div>
            </template>

            <!-- Pz/scatola, N° scatole e prezzo/unità comuni a tutti i tileGroup -->
            <template v-if="tileGroup">
              <div class="field field--span-4">
                <label for="cim-piecesperbox">Pz / scatola</label>
                <input
                  id="cim-piecesperbox"
                  type="number"
                  min="0"
                  v-model.number="form.piecesPerBox"
                  placeholder="inserisci"
                />
              </div>
              <div class="field field--span-4">
                <label for="cim-boxqty">N° scatole</label>
                <input
                  id="cim-boxqty"
                  type="number"
                  min="0"
                  v-model.number="form.boxQuantity"
                  placeholder="inserisci"
                />
              </div>
              <div class="field field--span-4">
                <!-- Prezzo/unità delle piastrelle: scrive in form.price,
                     il campo atteso da createItem (nessun campo parallelo). -->
                <label for="cim-price-unit"
                  >{{ priceLabel }} — IVA esclusa</label
                >
                <input
                  id="cim-price-unit"
                  type="number"
                  step="any"
                  min="0"
                  v-model.number="form.price"
                  placeholder="inserisci"
                />
              </div>
            </template>
          </template>

          <!-- Parquet -->
          <template v-else-if="category === 'parquet'">
            <div class="field field--span-4">
              <label for="cim-material">Materiale (specie legnosa)</label>
              <StatusSelect
                v-model="form.material"
                :options="parquetMaterialOptions"
                placeholder="Seleziona specie..."
                placeholderIcon="fa-tree"
              />
            </div>
            <div class="field field--span-4">
              <label for="cim-finish">Finitura (lavorazione)</label>
              <input
                id="cim-finish"
                type="text"
                v-model="form.finish"
                placeholder="es. Spazzolato naturale"
              />
            </div>
            <div class="field field--span-4">
              <label for="cim-scelta">Scelta</label>
              <StatusSelect
                v-model="form.specs.scelta"
                :options="parquetSceltaOptions"
                placeholder="Seleziona scelta..."
                placeholderIcon="fa-shapes"
              />
            </div>
            <div class="field field--span-3">
              <label for="cim-format">Formato</label>
              <input
                id="cim-format"
                type="text"
                v-model="form.formatLabel"
                placeholder="es. 65-70x440-490 mm"
              />
            </div>
            <div class="field field--span-3">
              <label for="cim-thickness">Spessore (mm)</label>
              <input
                id="cim-thickness"
                type="number"
                step="any"
                min="0"
                v-model.number="form.thicknessMm"
                placeholder="es. 10"
              />
            </div>
            <div class="field field--span-3">
              <label for="cim-width">Larghezza (cm)</label>
              <input
                id="cim-width"
                type="number"
                step="any"
                min="0"
                v-model.number="form.widthCm"
                placeholder="es. 6.5"
              />
            </div>
            <div class="field field--span-3">
              <label for="cim-length">Lunghezza (mm)</label>
              <input
                id="cim-length"
                type="number"
                step="any"
                min="0"
                v-model.number="form.lengthMm"
                placeholder="es. 440"
              />
            </div>
          </template>

          <!-- Altre categorie area (rivestimenti parete, laminati) -->
          <template v-else>
            <div class="field field--span-3">
              <label for="cim-format">Formato (es. 60x120)</label>
              <input
                id="cim-format"
                type="text"
                v-model="form.formatLabel"
                placeholder="es. 60x120"
              />
            </div>
            <div class="field field--span-3">
              <label for="cim-width">Larghezza (cm)</label>
              <input
                id="cim-width"
                type="number"
                step="any"
                min="0"
                v-model.number="form.widthCm"
                placeholder="inserisci"
              />
            </div>
            <div class="field field--span-3">
              <label for="cim-height">Altezza (cm)</label>
              <input
                id="cim-height"
                type="number"
                step="any"
                min="0"
                v-model.number="form.heightCm"
                placeholder="inserisci"
              />
            </div>
            <div class="field field--span-3">
              <label for="cim-thickness">Spessore (mm)</label>
              <input
                id="cim-thickness"
                type="number"
                step="any"
                min="0"
                v-model.number="form.thicknessMm"
                placeholder="es. 10"
              />
            </div>
            <div class="field field--span-6">
              <label for="cim-surface">Superficie</label>
              <input
                id="cim-surface"
                type="text"
                v-model="form.surface"
                placeholder="es. Matt, Lucido"
              />
            </div>
            <div class="field field--span-6">
              <label for="cim-color">Colore</label>
              <input
                id="cim-color"
                type="text"
                v-model="form.color"
                placeholder="inserisci"
              />
            </div>
            <div class="field field--span-6">
              <label for="cim-piecesperbox">Pz / scatola</label>
              <input
                id="cim-piecesperbox"
                type="number"
                min="0"
                v-model.number="form.piecesPerBox"
                placeholder="inserisci"
              />
            </div>
            <div class="field field--span-6">
              <label for="cim-boxqty">N° scatole</label>
              <input
                id="cim-boxqty"
                type="number"
                min="0"
                v-model.number="form.boxQuantity"
                placeholder="inserisci"
              />
            </div>
          </template>
        </template>

        <!-- ── Gruppo LINEAR (profili) ── -->
        <template v-if="categoryGroup === 'linear'">
          <div class="field field--span-4">
            <label for="cim-material">Materiale</label>
            <input
              id="cim-material"
              type="text"
              v-model="form.material"
              placeholder="es. Alluminio, Inox"
            />
          </div>
          <div class="field field--span-4">
            <label for="cim-length">Lunghezza (mm)</label>
            <input
              id="cim-length"
              type="number"
              step="any"
              min="0"
              v-model.number="form.lengthMm"
              placeholder="es. 2500"
            />
          </div>
          <div class="field field--span-4">
            <label for="cim-mlperbox">ML / confezione</label>
            <input
              id="cim-mlperbox"
              type="number"
              step="any"
              min="0"
              v-model.number="form.mlPerBox"
              placeholder="es. 2.5"
            />
          </div>
        </template>

        <!-- ── Gruppo GENERIC (altro) ── -->
        <template v-if="categoryGroup === 'generic'">
          <div class="field field--span-12">
            <label for="cim-notes">Note</label>
            <textarea
              id="cim-notes"
              v-model="form.notes"
              placeholder="inserisci"
              rows="3"
            />
          </div>
        </template>

        <hr />

        <div class="modal-actions">
          <button
            class="secondary-btn"
            type="button"
            :disabled="submitting"
            @click="backFromForm"
          >
            Indietro
          </button>
          <button
            class="primary-btn confirm-btn"
            type="submit"
            :disabled="!canSave || submitting"
            :title="!canSave ? 'Inserisci il nome articolo' : 'Salva articolo'"
          >
            <span v-if="submitting">Salvataggio…</span>
            <span v-else>Salva</span>
          </button>
        </div>
      </form>
    </template>
  </VueFinalModal>
</template>

<script>
import { mapActions } from "vuex/dist/vuex.cjs.js";
import { VueFinalModal } from "vue-final-modal";
import StatusSelect from "@/components/StatusSelect.vue";

// Sottoinsieme dei gruppi di CreateItemPage, limitato alle categorie ammesse
// dalla modale (piastrelle, rivestimenti parete, parquet, laminati, profili,
// altro). "bagno" è escluso: non è nell'ENUM category del backend.
const COLUMN_GROUPS = {
  area: ["piastrelle", "rivestimenti parete", "parquet", "laminati"],
  linear: ["profili"],
};

function getCategoryGroup(category) {
  for (const [group, cats] of Object.entries(COLUMN_GROUPS)) {
    if (cats.includes(category)) return group;
  }
  return "generic";
}

function emptyForm() {
  return {
    itemCode: "",
    itemName: "",
    brand: "",
    serie: "",
    price: 0,
    articleStatus: "attivo",
    subCategory: "",
    formatLabel: "",
    thicknessMm: null,
    surface: "",
    color: "",
    piecesPerBox: null,
    sqmPerBox: null,
    boxQuantity: null,
    finish: "",
    material: "",
    lengthMm: null,
    mlPerBox: null,
    widthCm: null,
    heightCm: null,
    specs: { scelta: "" },
    notes: "",
  };
}

export default {
  name: "CreateItemModal",
  components: { VueFinalModal, StatusSelect },
  props: {
    modelValue: { type: Boolean, default: false },
    // Testo cercato nel campo articolo del preventivo: precompila itemName
    // all'apertura. Opzionale.
    initialName: { type: String, default: "" },
  },
  emits: ["update:modelValue", "created"],
  data() {
    return {
      step: "category", // 'category' | 'form'
      submitting: false,
      category: "",
      categoryOptions: [
        { value: "piastrelle", label: "Piastrelle" },
        { value: "rivestimenti parete", label: "Rivestimenti Parete" },
        { value: "parquet", label: "Parquet" },
        { value: "laminati", label: "Laminati" },
        { value: "profili", label: "Profili" },
        { value: "altro", label: "Altro" },
      ],
      articleStatusOptions: [
        { value: "attivo", label: "Attivo", color: "#059669" },
        { value: "novita", label: "Novità", color: "#2563eb" },
        { value: "discontinuo", label: "Discontinuo", color: "#9ca3af" },
      ],
      subCategoryOptions: [
        { value: "Elementi Lineari", label: "Elementi Lineari" },
        { value: "Decori", label: "Decori" },
        { value: "Fondi", label: "Fondi" },
        { value: "Battiscopa", label: "Battiscopa" },
        { value: "Listelli", label: "Listelli" },
        { value: "Gradini", label: "Gradini" },
        { value: "Scalini", label: "Scalini" },
        { value: "Altri Pezzi Speciali", label: "Altri Pezzi Speciali" },
      ],
      parquetMaterialOptions: [
        { value: "", label: "— nessuna —" },
        { value: "ROVERE", label: "Rovere" },
        { value: "TEAK", label: "Teak" },
        { value: "IROKO", label: "Iroko" },
        { value: "DOUSSIE", label: "Doussié" },
      ],
      parquetSceltaOptions: [
        { value: "", label: "— nessuna —" },
        { value: "CERCHIO", label: "Cerchio" },
        { value: "QUADRATO", label: "Quadrato" },
        { value: "TRIANGOLO", label: "Triangolo" },
      ],
      form: emptyForm(),
    };
  },
  computed: {
    categoryGroup() {
      return getCategoryGroup(this.category);
    },
    tileGroup() {
      if (this.category !== "piastrelle") return null;
      const sub = this.form.subCategory;
      if (sub === "Elementi Lineari") return "linear";
      if (sub === "Decori" || sub === "Fondi") return "area";
      if (
        [
          "Battiscopa",
          "Listelli",
          "Gradini",
          "Scalini",
          "Altri Pezzi Speciali",
        ].includes(sub)
      )
        return "piece";
      return null;
    },
    derivedSalesUnit() {
      // Stessa derivazione di CreateItemPage (nessun "bath" qui).
      if (this.category === "parquet") return "M2";
      const g = this.tileGroup;
      if (g === "linear") return "ML";
      if (g === "area") return "M2";
      if (g === "piece") return "PZ";
      return null;
    },
    priceLabel() {
      const su = this.derivedSalesUnit;
      if (su === "ML") return "Prezzo / ML";
      if (su === "M2") return "Prezzo / M²";
      if (su === "PZ") return "Prezzo / pz";
      return "Prezzo";
    },
    // Categorie che richiedono la scelta di una sotto-categoria prima del form
    // (come nel flusso Listini: piastrelle). "bagno" è escluso dalla modale.
    requiresSubCategory() {
      return this.category === "piastrelle";
    },
    categoryLabel() {
      const opt = this.categoryOptions.find((c) => c.value === this.category);
      return opt ? opt.label : "";
    },
    // Classe del box modale, con modificatore per-step: gli step compatti
    // restano stretti, lo step "form" si allarga (vedi <style> non-scoped).
    contentClass() {
      return `confirm-modal-content create-item-modal create-item-modal--${this.step}`;
    },
    // Span dei campi Brand/Serie/Stato nella griglia: 3 colonne quando è
    // presente anche il Prezzo (categorie non-piastrelle → 4 campi in riga),
    // altrimenti 4 colonne (piastrelle → 3 campi in riga).
    mainColClass() {
      return this.category === "piastrelle" ? "field--span-4" : "field--span-3";
    },
    canSave() {
      if (!(this.form.itemName || "").trim()) return false;
      if (this.category === "piastrelle" && !this.form.subCategory)
        return false;
      return true;
    },
  },
  watch: {
    modelValue(open) {
      if (open) {
        // Precompila itemName dal testo cercato, senza sovrascrivere input utente.
        if (this.initialName && !this.form.itemName) {
          this.form.itemName = this.initialName;
        }
      }
    },
    category(next, prev) {
      // Cambiando categoria (fuori da piastrelle) azzera la sotto-categoria.
      if (prev === "piastrelle" && next !== "piastrelle") {
        this.form.subCategory = "";
      }
    },
  },
  methods: {
    ...mapActions(["createItem"]),

    reset() {
      this.step = "category";
      this.category = "";
      this.submitting = false;
      this.form = emptyForm();
    },
    close() {
      if (this.submitting) return;
      this.reset();
      this.$emit("update:modelValue", false);
    },
    // Step categoria → sotto-categoria (se richiesta) oppure direttamente form.
    goFromCategory() {
      if (!this.category) return;
      if (this.requiresSubCategory) {
        this.step = "subcategory";
        return;
      }
      this.enterForm();
    },
    // Step sotto-categoria → form (gate: sotto-categoria obbligatoria).
    goFromSubCategory() {
      if (this.requiresSubCategory && !this.form.subCategory) return;
      this.enterForm();
    },
    enterForm() {
      // Precompila itemName all'ingresso nel form se non ancora valorizzato.
      if (this.initialName && !this.form.itemName) {
        this.form.itemName = this.initialName;
      }
      this.step = "form";
    },
    backToCategory() {
      // Torna allo step categoria senza perdere i dati inseriti.
      this.step = "category";
    },
    backFromForm() {
      // Dal form torna alla sotto-categoria se prevista, altrimenti alla categoria.
      this.step = this.requiresSubCategory ? "subcategory" : "category";
    },

    async onSubmit() {
      if (!this.canSave || this.submitting) return;
      this.submitting = true;

      // Payload costruito identico a CreateItemPage.onSubmit (stessa
      // normalizzazione di specs), così il backend riceve la stessa forma.
      const payload = {
        ...this.form,
        category: this.category,
        salesUnit: this.derivedSalesUnit,
      };
      if (this.category === "parquet") {
        payload.specs = this.form.specs?.scelta
          ? { scelta: this.form.specs.scelta }
          : null;
      } else {
        delete payload.specs;
      }

      try {
        const res = await this.createItem(payload);
        this.$emit("created", {
          item: res?.data?.item ?? null,
          itemCode: payload.itemCode,
          message: res?.data?.message || "Articolo creato",
        });
        this.reset();
        this.$emit("update:modelValue", false);
      } catch (err) {
        this.handleError(err);
      } finally {
        this.submitting = false;
      }
    },

    handleError(err) {
      if (
        err.response?.status === 409 &&
        err.response?.data?.code === "ITEM_CODE_DUPLICATE"
      ) {
        this.$toast.error(
          "Questo codice è già utilizzato da un altro articolo."
        );
      } else {
        this.$toast.error(
          err.response?.data?.message ||
            err.response?.data?.error ||
            "Errore durante la creazione."
        );
      }
    },
  },
};
</script>

<style scoped>
/* === Step compatti (categoria / sotto-categoria) =========================
   Una sola riga: select a sinistra, gruppo pulsanti a destra. */
.cim-step-row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin: 14px 0 2px;
}
.cim-step-row__select {
  flex: 1 1 260px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
/* La select riempie la parte sinistra della riga */
.cim-step-row__select :deep(.status-select),
.cim-step-row__select :deep(.status-select__trigger) {
  width: 100%;
}
/* Neutralizza il margin-top globale di .modal-actions dentro la riga */
.modal-actions.cim-step-row__actions {
  margin: 0;
  flex: 0 0 auto;
}
@media (max-width: 560px) {
  .cim-step-row {
    flex-direction: column;
    align-items: stretch;
  }
  .cim-step-row__actions {
    justify-content: flex-end;
  }
}

/* === Step form: griglia solida a 12 colonne, scroll verticale interno ===== */
.create-item-form-grid {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 18px 24px;
  align-content: start;
  width: 100%;
  min-width: 0;
  margin: 14px 0 4px;
  max-height: calc(90vh - 150px);
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;
}
/* Campi = celle della griglia: mai più larghi della colonna assegnata */
.create-item-form-grid .field {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.field--span-3 {
  grid-column: span 3;
}
.field--span-4 {
  grid-column: span 4;
}
.field--span-6 {
  grid-column: span 6;
}
.field--span-12 {
  grid-column: span 12;
}
/* Separatore e barra azioni occupano l'intera riga */
.create-item-form-grid hr {
  grid-column: 1 / -1;
  width: 100%;
  border: none;
  border-top: 1px solid var(--border, #e5e7eb);
  margin: 2px 0;
}
.create-item-form-grid .modal-actions {
  grid-column: 1 / -1;
}
/* Controlli sempre a larghezza cella, niente overflow orizzontale */
.create-item-form-grid .field input,
.create-item-form-grid .field select,
.create-item-form-grid .field textarea {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
}
.create-item-form-grid .field :deep(.status-select),
.create-item-form-grid .field :deep(.status-select__trigger) {
  width: 100%;
  min-width: 0;
}
/* Mobile: colonna singola, ogni campo full-width */
@media (max-width: 768px) {
  .create-item-form-grid {
    grid-template-columns: 1fr;
  }
  .create-item-form-grid .field,
  .create-item-form-grid [class*="field--span"] {
    grid-column: 1 / -1;
    width: 100%;
  }
}
</style>

<!-- Dimensioni del box modale: NON-scoped perché il content-box di
     VueFinalModal è teleportato (senza data-v). Namespacizzato su
     .create-item-modal → nessun impatto sugli altri modali. -->
<style>
.documents-modal .confirm-modal-content.create-item-modal {
  width: min(92vw, 560px);
  max-width: 92vw;
  max-height: min(85vh, 620px);
}
/* Step form: molto più ampio e alto, come una mini-pagina desktop */
.documents-modal .confirm-modal-content.create-item-modal--form {
  width: min(1460px, 95vw);
  max-width: 98vw;
  max-height: 90vh;
}
/* Mobile: margini laterali 2.5% → larghezza 95vw, nessun overflow orizzontale */
@media (max-width: 768px) {
  .documents-modal .confirm-modal-content.create-item-modal,
  .documents-modal .confirm-modal-content.create-item-modal--form {
    width: 95vw;
    max-width: 95vw;
    margin: 0;
  }
}
</style>
